import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  checkAdminRights, 
  isAdminSession, 
  setupAdminSession, 
  clearAdminSession 
} from '@/utils/auth/adminUtils';
import { AdminUser } from '@/types/admin';

export const useAdmin = () => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState<AdminUser | null>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    const verifyAdmin = async () => {
      try {
        console.log('Verifying admin status...');
        setLoading(true);
        
        if (isAdminSession()) {
          console.log('Admin session found in local storage');
          
          try {
            const { data: sessionData } = await supabase.auth.getSession();
            if (sessionData.session) {
              console.log('Supabase session found, checking admin rights');
              const { data: adminData, error: adminError } = await supabase
                .from('admin_users')
                .select('*')
                .eq('user_id', sessionData.session.user.id)
                .maybeSingle();
              
              if (!adminError && adminData) {
                console.log('Admin data found:', adminData);
                setAdminData(adminData);
                setIsAdmin(true);
              } else {
                console.error('Admin data not found:', adminError);
                setIsAdmin(false);
                clearAdminSession();
              }
            } else {
              console.log('No Supabase session found despite admin session in localStorage');
              setIsAdmin(false);
              clearAdminSession();
            }
          } catch (err) {
            console.error('Error fetching admin data:', err);
            setIsAdmin(false);
            clearAdminSession();
          }
        } else {
          console.log('No admin session in localStorage, checking with server');
          
          try {
            const hasAdminRights = await checkAdminRights();
            
            if (hasAdminRights) {
              console.log('Admin rights confirmed by server');
              setIsAdmin(true);
              setupAdminSession();
            } else {
              console.log('No admin rights confirmed by server');
              setIsAdmin(false);
              clearAdminSession();
            }
          } catch (error) {
            console.error('Error checking admin rights with server:', error);
            setIsAdmin(false);
            clearAdminSession();
          }
        }
      } catch (error) {
        console.error('Error in admin verification process:', error);
        setIsAdmin(false);
        clearAdminSession();
      } finally {
        setLoading(false);
      }
    };
    
    verifyAdmin();
  }, []);
  
  const loginAdmin = async (email: string, password: string): Promise<boolean> => {
    console.log('Attempting admin login for:', email);
    setLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('Admin login error (auth):', error);
        clearAdminSession();
        throw error;
      }
      
      if (!data.user || !data.session) {
        console.error('No user or session returned after sign in');
        clearAdminSession();
        throw new Error('Login failed - no user data returned');
      }
      
      console.log('User authenticated, checking admin status');
      
      const { data: adminData, error: adminError } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', data.user.id)
        .maybeSingle();
      
      if (adminError) {
        console.error('Admin check error:', adminError);
        await supabase.auth.signOut();
        clearAdminSession();
        throw new Error('Error checking administrator access');
      }
      
      if (!adminData) {
        console.error('No admin record found');
        await supabase.auth.signOut();
        clearAdminSession();
        throw new Error('You do not have administrator access');
      }
      
      console.log('Admin status confirmed, setting up session');
      
      setupAdminSession();
      setIsAdmin(true);
      setAdminData(adminData);
      
      toast.success('Admin login successful');
      navigate('/administrator/dashboard');
      return true;
      
    } catch (error: any) {
      console.error('Admin login process error:', error);
      setIsAdmin(false);
      clearAdminSession();
      toast.error('Login failed: ' + (error.message || 'Authentication error'));
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  const logoutAdmin = useCallback(async () => {
    setLoading(true);
    try {
      console.log('useAdmin: Logging out admin user');
      
      clearAdminSession();
      await supabase.auth.signOut();
      
      setIsAdmin(false);
      setAdminData(null);
      
      console.log('Admin logout successful, redirecting to login page');
      toast.success('Logged out successfully');
      
      window.location.href = '/administrator/login';
    } catch (error: any) {
      console.error('Logout error:', error);
      toast.error('Failed to log out');
    } finally {
      setLoading(false);
    }
  }, []);
  
  return {
    isAdmin,
    adminData,
    loading,
    loginAdmin,
    logoutAdmin
  };
};

export default useAdmin;
